/**
 * Frontend mirror of cowork/ui/model_limits.py.
 *
 * Keep these tables in sync — the backend emits ``context_percent`` computed
 * against these same limits, but we recompute client-side in two cases so the
 * context-occupancy bar stays accurate without waiting for the next turn:
 *  (1) On conversation switch, so the bar reflects the reopened chat's size
 *      immediately.
 *  (2) On model switch from the ModelSelector dropdown, so the bar
 *      re-evaluates against the new model's window — e.g. a Sonnet (1M)
 *      conversation that's accumulated 500K tokens jumps from ~50% to ~100%
 *      the instant the user picks gpt-4o-mini (128K).
 *
 * The bar is informational only — the Claude Agent SDK auto-compacts on its
 * own as the context fills; we no longer nudge the user to ``/compact``.
 *
 * Drift between this table and the backend is caught by
 * ``tests/test_model_limits_frontend_mirror.py``.
 *
 * The table covers Claude/OpenAI/Gemini ids only. OpenRouter windows come
 * from the live catalog (``modelCatalogStore``) — same split the backend uses.
 */

import { openRouterContextWindow } from "@/stores/modelCatalogStore";

export const MODEL_CONTEXT_LIMITS: Record<string, number> = {
  // Anthropic
  "claude-haiku-4-5-20251001": 200_000,
  "claude-sonnet-4-6":         1_000_000,
  // opus-4-6 retired from the picker but kept for conversations pinned to it.
  "claude-opus-4-6":           1_000_000,
  "claude-opus-4-7":           1_000_000,
  "claude-opus-4-8":           1_000_000,
  "claude-opus-5":             1_000_000,
  "claude-fable-5":            1_000_000,
  "claude-fable-5-1":          1_000_000,
  // OpenAI GPT-5.5 family
  "openai-gpt-5-5":            1_050_000,
  "openai-gpt-5-5-pro":        1_050_000,
  // OpenAI GPT-5.4 family
  "openai-gpt-5-4":            1_000_000,
  "openai-gpt-5-4-mini":       400_000,
  "openai-gpt-5-4-nano":       400_000,
  // OpenAI GPT-4.1 family
  "openai-gpt-4-1":            1_000_000,
  "openai-gpt-4-1-mini":       1_000_000,
  "openai-gpt-4-1-nano":       1_000_000,
  // o-series reasoning
  "openai-o3":                 200_000,
  "openai-o3-pro":             200_000,
  "openai-o4-mini":            200_000,
  // gpt-4o legacy
  "openai-gpt-4o":             128_000,
  "openai-gpt-4o-mini":        128_000,
  // Google Gemini 3.x — preview
  "gemini-3-1-pro":            1_000_000,
  "gemini-3-5-flash":          1_000_000,
  "gemini-3-flash":            1_000_000,
  "gemini-3-1-flash-lite":     1_000_000,
  // Google Gemini 2.5 — stable
  "gemini-2-5-pro":            1_000_000,
  "gemini-2-5-flash":          1_000_000,
  "gemini-2-5-flash-lite":     1_000_000,
};

export const DEFAULT_CONTEXT_LIMIT = 200_000;

/** Prefix for OpenRouter-routed ids. Mirrors ``openrouter_catalog.OPENROUTER_PREFIX``. */
const OPENROUTER_PREFIX = "openrouter/";

export function getContextLimit(model: string | null | undefined): number {
  if (!model) return DEFAULT_CONTEXT_LIMIT;
  // OpenRouter ids are NOT in the table above and never will be — there are
  // hundreds and their windows are published by OpenRouter, so enumerating
  // them here would only go stale. Read the fetched catalog instead, exactly
  // like the backend's ``model_limits.get_context_limit`` does. Without this
  // every OpenRouter model reported the 200K default regardless of its real
  // window (Kimi K3 is 1M, and showed as 200K).
  if (model.startsWith(OPENROUTER_PREFIX)) {
    return openRouterContextWindow(model) ?? DEFAULT_CONTEXT_LIMIT;
  }
  return MODEL_CONTEXT_LIMITS[model] ?? DEFAULT_CONTEXT_LIMIT;
}

export function computeContextPercent(
  tokens: number | null | undefined,
  model: string | null | undefined,
): number {
  if (!tokens || tokens <= 0) return 0;
  const limit = getContextLimit(model);
  const pct = (tokens / limit) * 100;
  return Math.round(Math.max(0, Math.min(pct, 100)) * 10) / 10;
}
